// ============================================================================
// BOQ — unit canonicalisation + effective unit for a project line
// ============================================================================
// Source BOQs spell the same unit a dozen ways ("Cum", "Cu.m", "Sqm",
// "Sq m", "Nos.", "No's", "Rmt", "Rm", …). `canonicalUnit()` folds them to a
// single compact display convention so every table reads consistently:
//
//   length  → m1     area → m2     volume → m3     count → #
//   (imperial: ft1 / ft2 / ft3)    mass → kg / t   lump → LS …
//
// Apply it both at display time (so existing rows look uniform) and in the
// import / create chokepoints (so stored units are canonical going forward).
// Unknown / bespoke units pass through untouched so nothing is ever lost.
// ============================================================================

const SUPERSCRIPT = { '¹': '1', '²': '2', '³': '3' };

// Normalise a raw unit to a comparison key: fold superscripts, lowercase, and
// strip every non-alphanumeric character (dots, spaces, apostrophes, slashes).
//   "Sq.m." → "sqm"   "No's" → "nos"   "Cu.m" → "cum"   "m²" → "m2"
function unitKey(raw) {
  return String(raw == null ? '' : raw)
    .replace(/[¹²³]/g, (c) => SUPERSCRIPT[c])
    .toLowerCase()
    .replace(/[^a-z0-9]/g, '');
}

// Normalised key → canonical unit. Note running-metre keys are "rmt"/"rm"/"m",
// so a bare "mt" / "MT" is treated as metric tonne (which is how the source
// BOQs use it — running metre is always written "Rmt"/"Rm").
const UNIT_CANON = {
  // length (running metre) → m1
  m1: 'm1', m: 'm1', mtr: 'm1', mtrs: 'm1', mtr2: 'm1', meter: 'm1', metre: 'm1', meters: 'm1', metres: 'm1',
  rm: 'm1', rmt: 'm1', rmts: 'm1', rmtr: 'm1', rmtrs: 'm1', runm: 'm1', runningm: 'm1', runningmeter: 'm1', runningmetre: 'm1', lm: 'm1', lmt: 'm1',
  // area → m2
  m2: 'm2', sqm: 'm2', sqmt: 'm2', sqmts: 'm2', sqmtr: 'm2', sqmtrs: 'm2', sqmeter: 'm2', sqmetre: 'm2', sqmeters: 'm2', sqmetres: 'm2', squaremetre: 'm2', squaremeter: 'm2', sm: 'm2',
  // volume → m3
  m3: 'm3', cum: 'm3', cumt: 'm3', cumtr: 'm3', cubicm: 'm3', cubicmeter: 'm3', cubicmetre: 'm3', cubm: 'm3',
  // count / number → #
  '#': '#', no: '#', nos: '#', number: '#', numbers: '#', num: '#', each: '#', ea: '#',
  pc: '#', pcs: '#', piece: '#', pieces: '#', pt: '#', pts: '#', point: '#', points: '#',
  unit: '#', units: '#', set: '#', sets: '#', item: '#', items: '#',
  // imperial
  ft1: 'ft1', rft: 'ft1', runningfeet: 'ft1', rfeet: 'ft1',
  ft2: 'ft2', sqft: 'ft2', sft: 'ft2', squarefeet: 'ft2', squarefoot: 'ft2',
  ft3: 'ft3', cft: 'ft3', cuft: 'ft3', cubicfeet: 'ft3',
  // mass
  kg: 'kg', kgs: 'kg', kilogram: 'kg', kilograms: 'kg',
  t: 't', mt: 't', ton: 't', tons: 't', tonne: 't', tonnes: 't', metricton: 't', qtl: 'qtl', quintal: 'qtl',
  // volume (liquid)
  l: 'L', ltr: 'L', ltrs: 'L', litre: 'L', litres: 'L', liter: 'L', liters: 'L',
  // lump sum / time
  ls: 'LS', lumpsum: 'LS', lump: 'LS', sum: 'LS', job: 'LS',
  day: 'day', days: 'day', hr: 'hr', hrs: 'hr', hour: 'hr', hours: 'hr',
  // pairs
  pair: 'pair', pairs: 'pair', pr: 'pair',
};

// Fold a raw unit string to the canonical display convention. Unknown units are
// returned trimmed but otherwise unchanged.
export function canonicalUnit(raw) {
  if (raw == null) return '';
  const trimmed = String(raw).trim();
  if (!trimmed) return '';
  return UNIT_CANON[unitKey(trimmed)] || trimmed;
}

// A line's displayed/exported unit follows its measure basis when one is set,
// otherwise falls back to the library item version's unit (manual lines).
const BASIS_UNIT = {
  running_m: 'm1',
  plan_area: 'm2',
  elevation_area: 'm2',
  volume: 'm3',
  count: '#',
  // Zone gross-area bases used by SD area-rate (allowance) lines.
  carpet_area: 'm2',
  built_up_area: 'm2',
  construction_area: 'm2',
};

export function unitForBasis(basis) {
  return BASIS_UNIT[basis] || null;
}

// Lump-sum line types carry their own unit, independent of measure basis.
const LUMP_UNIT = { provisional_sum: 'LS', pc_sum: 'LS', dayworks: 'day' };

// line: { unit, measureBasis, lineType, version: { unit } }
export function effectiveUnit(line) {
  if (!line) return '';
  // An explicit unit stored on the line itself wins (SD allowance lines).
  if (line.unit) return canonicalUnit(line.unit);
  // True lump-sum line types carry their own unit, independent of basis.
  if (line.lineType === 'provisional_sum' || line.lineType === 'pc_sum' || line.lineType === 'dayworks') {
    return LUMP_UNIT[line.lineType] || 'LS';
  }
  // Measured and allowance lines resolve from the measure basis.
  return unitForBasis(line.measureBasis) || canonicalUnit(line.version?.unit) || '';
}
